import React from 'react';
import { Card, CardContent, Box, Typography, Stack, Skeleton } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

/**
 * A single headline metric.
 *
 * Replaces the previous gradient-filled cards: the surface stays neutral and
 * colour is used only for the icon and the trend indicator, so the numbers
 * themselves carry the emphasis.
 */
const StatCard = ({
  label,
  value,
  caption,
  icon,
  color = 'primary.main',
  trend,
  loading = false,
}) => {
  const trendDirection =
    typeof trend === 'number' ? (trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat') : null;

  const TrendIcon =
    trendDirection === 'up' ? TrendingUp : trendDirection === 'down' ? TrendingDown : TrendingFlat;

  const trendColor =
    trendDirection === 'up'
      ? 'success.main'
      : trendDirection === 'down'
      ? 'error.main'
      : 'text.secondary';

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'border-color 200ms, transform 200ms',
        '&:hover': { borderColor: 'rgba(0, 82, 255, 0.45)' },
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="overline" color="text.secondary" noWrap>
            {label}
          </Typography>
          {icon && (
            <Box
              aria-hidden
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color,
                // Tint derived from the icon colour keeps each card distinct
                // without resorting to full-bleed gradients.
                bgcolor: 'rgba(200, 216, 255, 0.10)',
                '& svg': { fontSize: 19 },
              }}
            >
              {icon}
            </Box>
          )}
        </Stack>

        {loading ? (
          <Skeleton variant="text" width="60%" height={44} />
        ) : (
          <Typography
            variant="h3"
            component="p"
            sx={{ fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}
          >
            {value}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, minHeight: 20 }}>
          {trendDirection && !loading && (
            <>
              <TrendIcon sx={{ fontSize: 16, color: trendColor }} />
              <Typography variant="caption" sx={{ color: trendColor, fontWeight: 600 }}>
                {trend > 0 ? '+' : ''}
                {trend}%
              </Typography>
            </>
          )}
          {caption && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {caption}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default StatCard;
